import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_DELETE, DBRequest, useConfig } from '../../../api/api';
import { hasPermissions } from '../../../auth/hasPermissions';
import NoDataAnimation from '../../../components/animation/NoDataAnimation';
import CardContainer from '../../../components/cards/layout/CardContainer';
import ExoHistory from '../../../components/exo/ExoHistory';
import { ExoTableToolbarItem } from '../../../components/exo/ExoTableToolbar';
import HourLabel from '../../../components/label/HourLabel';
import MoneyLabel from '../../../components/label/MoneyLabel';
import ExoAlert from '../../../components/exo/ExoAlert';
import { ContractNode } from './contract-elements/ContractNode';

import UploadSigendContract from './contract-elements/UploadSignedContract';
import EditWorkContract from './contract-elements/EditWorkContract';
import QuickEditContract from './contract-elements/QuickEditContract';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import EditContract from './contract-elements/EditContract';

const EmployeeContract = ({ employeeInfo, onOpen, onChange }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const contracts = employeeInfo.contracts;

  const [activeContract, setActiveContract] = useState(null);
  const [contractToSign, setContractToSign] = useState(null);
  const [contractToEdit, setContractToEdit] = useState(null);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);

  useEffect(() => {
    if (contracts) {
      contracts.forEach((element) => {
        if (element.isActive) setActiveContract(element);
      });
    }
  }, [contracts]);

  function handleContractChange(updatedContract) {
    const newContracts = updateOrCreate(contracts, updatedContract);
    onChange({ ...employeeInfo, contracts: newContracts });
  }

  function handleDeleteContract() {
    if (!contractToDelete) return;
    DBRequest({
      config,
      path: `employee-contracts/${contractToDelete.id}`,
      method: API_DELETE(),
      onResponse: () => {
        const newContracts = contracts.filter(
          (c) => c.id !== contractToDelete.id,
        );
        onChange({
          ...employeeInfo,
          contracts: newContracts.length > 0 ? newContracts : null,
        });
        setContractToDelete(null);
      },
    });
  }

  function handleNewContractFromQuickEdit(newContract) {
    const updatedContracts = contracts.map((c) =>
      c.isActive ? { ...c, isActive: 0 } : c,
    );
    onChange({
      ...employeeInfo,
      contracts: [newContract, ...updatedContracts],
    });
  }

  if (!contracts) {
    return (
      <Box className="w-full h-full flex justify-center items-center relative">
        {hasPermissions('employee-contract', 'write') && (
          <ExoTableToolbarItem
            className=" absolute top-2 left-2"
            icon={<HistoryEduIcon />}
            text={t('New Contract')}
            onClick={onOpen}
          />
        )}
        <NoDataAnimation />
      </Box>
    );
  }

  const entries = contracts.map((contract) => {
    return {
      data: {
        ...contract,
        onSign: setContractToSign,
        onEdit: setContractToEdit,
        onDelete: setContractToDelete,
      },
      date: contract.startDate,
      isActive: Boolean(contract.isActive),
      label: contract.position,
    };
  });

  return (
    <Box className="flex flex-col gap-3 items-start w-full relative @container ">
      {hasPermissions('employee-contract', 'write') && (
        <Box className="flex gap-2">
          <ExoTableToolbarItem
            icon={<HistoryEduIcon />}
            text={t('New workcontract')}
            onClick={onOpen}
          />
          {activeContract && (
            <ExoTableToolbarItem
              icon={<TuneIcon />}
              text={t('Adjust Contract')}
              onClick={() => setQuickEditOpen(true)}
            />
          )}
        </Box>
      )}
      {activeContract && (
        <>
          <Box className="grid grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3 w-full gap-4">
            <InfoCard
              label={t('Salery')}
              Icon={AttachMoneyIcon}
              value={activeContract.salary}
              type="money"
            />
            <InfoCard
              label={t('Hours per Week')}
              Icon={AccessTimeIcon}
              value={activeContract.hoursPerWeek}
              type="hours"
            />
            <InfoCard
              label={t('Vacation')}
              Icon={AccessTimeIcon}
              value={activeContract.vacation}
            />
          </Box>
        </>
      )}
      <ExoHistory entries={entries} EntryNode={ContractNode} />
      <UploadSigendContract
        open={Boolean(contractToSign)}
        onClose={() => setContractToSign(null)}
        contract={contractToSign}
        onUpload={handleContractChange}
      />
      {/* <EditWorkContract
        open={Boolean(contractToEdit)}
        onClose={() => setContractToEdit(null)}
        contract={contractToEdit}
        employeeId={employeeInfo.id}
        onUpdate={handleContractChange}
      /> */}
      <EditContract
        open={Boolean(contractToEdit)}
        onClose={() => setContractToEdit(null)}
        contract={contractToEdit}
        employeeId={employeeInfo.id}
        onUpdate={handleContractChange}
        employeeInfo={employeeInfo}
      />
      <QuickEditContract
        open={quickEditOpen}
        onClose={() => setQuickEditOpen(false)}
        contract={activeContract}
        employeeId={employeeInfo.id}
        onCreate={handleNewContractFromQuickEdit}
        employeeInfo={employeeInfo}
      />
      <ExoAlert
        open={Boolean(contractToDelete)}
        alert={{
          message: t('Are you sure you want to delete this contract?'),
          type: 'warning',
          submitLabel: t('Delete'),
          cancelLabel: t('Cancel'),
        }}
        onSubmit={handleDeleteContract}
        onClose={() => setContractToDelete(null)}
      />
    </Box>
  );
};

const InfoCard = ({ Icon, label, value, type }) => {
  return (
    <CardContainer className="flex flex-col gap-2 justify-center items-center">
      <Icon />
      <Typography textAlign="center">{label}</Typography>
      <Typography className="monospace" variant="h3" textAlign="center">
        {type === 'money' && <MoneyLabel money={value || 0} />}
        {type === 'hours' && <HourLabel minutes={value * 60} />}
        {!type && value}
      </Typography>
    </CardContainer>
  );
};

export default EmployeeContract;
